import { useTranslation } from "react-i18next";
import Box from "../../generic/components/box";
import { useAuthState } from "/app/src/contexts/authentication";
import useSetting from "../setting";
import { Button, Form, Select, SelectItem, SelectSection } from "@heroui/react";
import { useController, useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";

interface FormValues {
  explorerNavigation: string[];
}

/**
 * Explorer component for configuring pinned navigation items in the Explorer.
 * @returns {JSX.Element} The rendered Explorer component.
 */
export default function Explorer() {
  const { t } = useTranslation();
  const { user } = useAuthState();

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerNavigation",
    userId: user?.id,
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      explorerNavigation: settingValue ? settingValue.split(",") : [],
    },
  });

  useEffect(() => {
    const initial = Array.isArray(settingValue)
      ? settingValue
      : settingValue
        ? settingValue.split(",")
        : [];
    reset({ explorerNavigation: initial });
  }, [settingValue, reset]);

  const onSubmit = useCallback(
    async (data: FormValues) => {
      const values = data?.explorerNavigation || [];
      const valueToSave = Array.isArray(values) ? values.join(",") : values;
      await createUpdateSetting({
        name: "explorerNavigation",
        value: valueToSave,
        userId: user?.id,
      });
      // clear dirty state after successful save
      reset({ explorerNavigation: values });
    },
    [createUpdateSetting, reset, user?.id],
  );

  const { field } = useController<FormValues>({
    name: "explorerNavigation",
    control,
    defaultValue: Array.isArray(settingValue)
      ? settingValue
      : settingValue
        ? settingValue.split(",")
        : [],
  });

  const orderOptions = useMemo(
    () => [
      { id: "batches", label: t("translation:batches") },
      { id: "orders", label: t("translation:orders") },
      { id: "order_lines", label: t("translation:order_lines") },
      { id: "transactions", label: t("translation:transactions") },
    ],
    [t],
  );

  const materialOptions = useMemo(
    () => [
      { id: "materials", label: t("translation:materials") },
      {
        id: "material_properties",
        label: t("translation:material_properties"),
      },
    ],
    [t],
  );

  const locationOptions = useMemo(
    () => [
      { id: "bins", label: t("translation:bins") },
      { id: "locations", label: t("translation:locations") },
      { id: "warehouses", label: t("translation:warehouses") },
      { id: "zones", label: t("translation:zones") },
    ],
    [t],
  );

  const powerPickOptions = useMemo(
    () => [
      { id: "cost_centers", label: t("translation:cost_centers") },
      { id: "parameter_values", label: t("translation:parameter_values") },
    ],
    [t],
  );

  const handleSelectionChange = useCallback(
    (options: Set<string>) => {
      field.onChange(Array.from(options));
    },
    [field],
  );

  return (
    <Box>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 w-full"
      >
        <div className="flex justify-between w-full">
          <h1>{t("translation:explorer")}</h1>

          <Button
            isDisabled={!isDirty || isSubmitting}
            type="submit"
            color="primary"
          >
            {t("translation:save")}
          </Button>
        </div>
        <Select
          labelPlacement="outside"
          label="Pinned Explorer Navigation Items"
          selectionMode="multiple"
          selectedKeys={field.value || []}
          onSelectionChange={handleSelectionChange}
          className="w-[50%]"
        >
          <SelectSection showDivider title={t("translation:orders")}>
            {orderOptions.map((option) => (
              <SelectItem key={option.id}>{option.label}</SelectItem>
            ))}
          </SelectSection>
          <SelectSection showDivider title={t("translation:materials")}>
            {materialOptions.map((option) => (
              <SelectItem key={option.id}>{option.label}</SelectItem>
            ))}
          </SelectSection>
          <SelectSection showDivider title={t("translation:locations")}>
            {locationOptions.map((option) => (
              <SelectItem key={option.id}>{option.label}</SelectItem>
            ))}
          </SelectSection>
          <SelectSection showDivider title={t("translation:power_pick")}>
            {powerPickOptions.map((option) => (
              <SelectItem key={option.id}>{option.label}</SelectItem>
            ))}
          </SelectSection>
        </Select>
      </Form>
    </Box>
  );
}
